import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { KantoorSidebar } from "@/components/KantoorSidebar";
import { MobileBottomTabs } from "@/components/MobileBottomTabs";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import {
  ArrowLeft, Building2, MapPin, Users, Hash, Briefcase, CheckCircle2, XCircle,
  Clock, Video, Phone, Info, ChevronRight,
} from "lucide-react";

const TIME_SLOTS = ["09:00", "10:30", "13:00", "14:30", "16:00"];

const buildDates = (count = 14) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const x = new Date(d);
    x.setDate(d.getDate() + i);
    if (x.getDay() === 0 || x.getDay() === 6) continue;
    out.push(x);
    if (out.length >= 10) break;
  }
  return out;
};

const formatDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const MatchDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allMatches, setAllMatches] = useState([]);
  const [scheduling, setScheduling] = useState(false);

  const dates = useMemo(() => buildDates(20), []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [via, setVia] = useState("videogesprek");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    Promise.all([api.get(`/matches/${id}`), api.get("/matches/me")])
      .then(([a, b]) => {
        setMatch(a.data);
        setAllMatches(b.data);
        if (a.data.gesprek) {
          setSelectedDate(a.data.gesprek.datum);
          setSelectedTime(a.data.gesprek.tijdstip);
          setVia(a.data.gesprek.via);
        } else {
          setSelectedDate(formatDate(dates[0]));
        }
      })
      .catch(() => navigate("/dashboard"))
      .finally(() => setLoading(false));
  }, [id, user, authLoading, navigate, dates]);

  if (authLoading || loading || !match) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#F5A623] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const klant = match.klant || {};
  const lang = i18n.language?.startsWith("en") ? "en-US" : "nl-NL";

  const schedule = async () => {
    if (!selectedDate || !selectedTime) return;
    setScheduling(true);
    try {
      await api.post("/gesprekken", { match_id: match.id, datum: selectedDate, tijdstip: selectedTime, via });
      const refreshed = await api.get(`/matches/${id}`);
      setMatch(refreshed.data);
    } catch { alert(t("common.error_generic")); } finally { setScheduling(false); }
  };

  const markResultaat = async (resultaat) => {
    try {
      await api.patch(`/matches/${id}/resultaat`, { resultaat });
      const r = await api.get(`/matches/${id}`);
      setMatch(r.data);
    } catch { alert(t("common.error_generic")); }
  };

  const newCount = allMatches.filter((m) => m.status === "nieuw").length;
  const gesprekkenCount = allMatches.filter((m) => m.gesprek && m.status === "gesprek_gepland").length;
  const eerdere = allMatches.filter((m) => m.id !== match.id);

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <KantoorSidebar badges={{ newMatches: newCount, newGesprekken: gesprekkenCount }} />
      <main className="flex-1 min-w-0">
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <Logo size="sm" />
          <Link to="/dashboard" className="text-sm font-semibold text-[#1A2B4A]" data-testid="mobile-back">{t("common.back")}</Link>
        </div>
        <div className="p-4 md:p-8 pb-24 lg:pb-8 max-w-6xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#1A2B4A]" data-testid="back-to-dashboard">
            <ArrowLeft size={16} /> {t("match_detail.back")}
          </Link>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("match_detail.title")} · {klant.bedrijfsnaam}</h1>
              <p className="text-slate-600 mt-1">{t("match_detail.subtitle")}</p>
            </div>
          </div>

          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="beecenter-card p-6" data-testid="klant-info-panel">
              <h2 className="font-heading font-bold text-[#1A2B4A]">{t("match_detail.klant_panel")}</h2>
              <div className="mt-5 space-y-3 text-sm">
                <Row icon={Building2} label={t("match_detail.company_name")} value={klant.bedrijfsnaam} />
                <Row icon={Hash} label={t("match_detail.kvk")} value={klant.kvk || "—"} />
                <Row icon={Briefcase} label={t("match_detail.branche")} value={klant.branche} />
                <Row icon={Users} label={t("match_detail.medewerkers")} value={klant.medewerkers} />
                <Row icon={Building2} label={t("match_detail.rechtsvorm")} value={klant.rechtsvorm} />
                <Row icon={MapPin} label={t("match_detail.locatie")} value={klant.locatie} />
              </div>
              {klant.extra_info && (
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("match_detail.help_question")}</h3>
                  <p className="text-sm text-slate-700 mt-2 leading-relaxed">{klant.extra_info}</p>
                </div>
              )}
              <div className="mt-6 pt-5 border-t border-slate-100">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("match_detail.needs")}</h3>
                <ul className="mt-3 space-y-2">
                  {(klant.behoeften || []).map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={16} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="beecenter-card p-6" data-testid="schedule-panel">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-900 text-sm">{t("match_detail.good_news")}</div>
                    <p className="text-xs text-emerald-800 mt-1">{t("match_detail.good_news_desc")}</p>
                  </div>
                </div>
              </div>

              <h2 className="font-heading font-bold text-[#1A2B4A] mt-6">{t("match_detail.schedule_panel")}</h2>

              <div className="mt-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">{t("match_detail.date")}</h3>
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                  {dates.map((d) => {
                    const ds = formatDate(d);
                    const active = selectedDate === ds;
                    const dayName = d.toLocaleDateString(lang, { weekday: "short" });
                    const month = d.toLocaleDateString(lang, { month: "short" });
                    return (
                      <button key={ds} onClick={() => setSelectedDate(ds)}
                        className={`flex-shrink-0 w-16 py-3 rounded-xl border-2 transition-all flex flex-col items-center ${
                          active ? "border-[#F5A623] bg-[#F5A623] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`} data-testid={`date-${ds}`}>
                        <span className="text-[10px] uppercase font-bold">{dayName}</span>
                        <span className="text-lg font-bold leading-none mt-0.5">{d.getDate()}</span>
                        <span className="text-[10px] mt-0.5">{month}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">{t("match_detail.time")}</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {TIME_SLOTS.map((tt) => {
                    const active = selectedTime === tt;
                    return (
                      <button key={tt} onClick={() => setSelectedTime(tt)}
                        className={`py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                          active ? "border-[#F5A623] bg-[#F5A623] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`} data-testid={`time-${tt}`}>
                        {tt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-3 gap-4 pt-5 border-t border-slate-100">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5"><Clock size={12} /> {t("match_detail.duration")}</div>
                  <div className="text-sm font-semibold text-[#1A2B4A] mt-1">{t("match_detail.minutes")}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1.5">{t("match_detail.via")}</div>
                  <div className="flex gap-2">
                    <button onClick={() => setVia("videogesprek")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-all ${via === "videogesprek" ? "border-[#F5A623] bg-[#F5A623]/10" : "border-slate-200 bg-white"}`}
                      data-testid="via-video">
                      <Video size={14} /> {t("match_detail.via_video")}
                    </button>
                    <button onClick={() => setVia("telefoon")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-all ${via === "telefoon" ? "border-[#F5A623] bg-[#F5A623]/10" : "border-slate-200 bg-white"}`}
                      data-testid="via-phone">
                      <Phone size={14} /> {t("match_detail.via_phone")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <Info size={16} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#7a4d05]">{t("match_detail.tip")}</p>
              </div>

              <button onClick={schedule} disabled={!selectedDate || !selectedTime || scheduling}
                className="btn-amber w-full mt-6 !py-3.5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-schedule">
                {scheduling ? t("common.loading") : match.gesprek ? t("match_detail.update_btn") : t("match_detail.schedule_btn")}
              </button>
              <button className="mt-3 w-full text-center text-sm text-slate-500 hover:text-[#1A2B4A]" data-testid="btn-cancel">{t("common.cancel")}</button>

              {match.status === "gesprek_gepland" && (
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">{t("match_detail.after_call")}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => markResultaat("match")} className="bg-[#22C55E] hover:bg-[#16a34a] text-white rounded-lg py-2.5 px-4 font-semibold text-sm flex items-center justify-center gap-2" data-testid="btn-mark-match">
                      <CheckCircle2 size={16} /> {t("match_detail.mark_match")}
                    </button>
                    <button onClick={() => markResultaat("geen_match")} className="bg-rose-500 hover:bg-rose-600 text-white rounded-lg py-2.5 px-4 font-semibold text-sm flex items-center justify-center gap-2" data-testid="btn-mark-no-match">
                      <XCircle size={16} /> {t("match_detail.mark_no_match")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="beecenter-card p-6" data-testid="match-info-panel">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">
                <CheckCircle2 size={14} /> {t("match_detail.match_confirmed")}
              </span>
              <h2 className="font-heading font-bold text-[#1A2B4A] mt-4">{t("match_detail.info_panel")}</h2>
              <p className="text-sm text-slate-600 mt-2">{t("match_detail.info_desc")}</p>

              <h3 className="font-heading font-semibold text-[#1A2B4A] mt-6">{t("match_detail.how_works")}</h3>
              <ol className="mt-3 space-y-3">
                {[t("match_detail.how_1"), t("match_detail.how_2"), t("match_detail.how_3"), t("match_detail.how_4")].map((tx, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-700">
                    <span className="w-6 h-6 rounded-full bg-[#F5A623] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <span>{tx}</span>
                  </li>
                ))}
              </ol>
              <a href="#" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#F5A623] hover:underline">
                {t("match_detail.more_info")} <ChevronRight size={14} />
              </a>
            </div>
          </div>

          <div className="mt-8 beecenter-card overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-heading font-bold text-[#1A2B4A]">{t("match_detail.previous_title")}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.company")}</th>
                    <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.match_date")}</th>
                    <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.status")}</th>
                    <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.gesprek")}</th>
                    <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.result")}</th>
                    <th className="text-right px-5 py-3 font-semibold">{t("dashboard.table.fee")}</th>
                  </tr>
                </thead>
                <tbody>
                  {eerdere.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-4 font-semibold text-[#1A2B4A]">{m.klant?.bedrijfsnaam}</td>
                      <td className="px-5 py-4 text-slate-600">{new Date(m.created_at).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td className="px-5 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">{t(`dashboard.status.${m.status}`)}</span>
                      </td>
                      <td className="px-5 py-4 text-slate-600">{m.gesprek ? `${m.gesprek.datum} · ${m.gesprek.tijdstip}` : "—"}</td>
                      <td className="px-5 py-4 text-slate-600">{m.status === "match" ? t("dashboard.result_match") : m.status === "geen_match" ? t("dashboard.result_no_match") : "—"}</td>
                      <td className="px-5 py-4 text-right font-semibold text-[#1A2B4A]">{m.vergoeding ? `€${m.vergoeding.toFixed(0)}` : "—"}</td>
                    </tr>
                  ))}
                  {eerdere.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">{t("match_detail.previous_empty")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <MobileBottomTabs badges={{ newMatches: newCount, newGesprekken: gesprekkenCount }} />
      </main>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Icon size={14} className="text-slate-500" /></div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-semibold text-[#1A2B4A] truncate">{value}</div>
    </div>
  </div>
);

export default MatchDetail;
